/**
 * KONFIGURACJA AMBIENT FX — particle effects per-biom (Step 3.B.2).
 *
 * Każdy biom może mieć ambient atmosfere przez wiele warstw ParticleEmitter'ów:
 *  - Mountain: padający śnieg (3 warstwy parallax — daleki, średni, bliski)
 *  - Przyszłe: las → unoszące się liście, wulkan → popiół, niebo → chmury
 *
 * **Layer = jeden ParticleEmitter** z własnym texture, density, depth.
 * Wiele warstw daje wrażenie głębi (parallax): tylne wolne i blade, przednie
 * szybkie i wyraźne. Wszystkie warstwy korzystają ze WSPÓLNEJ wind oscillation
 * (jeden sin wave) ale każda ma własną amplitudę — najsilniej bujają się
 * cząstki bliskiej warstwy.
 *
 * **Camera-follow spawn:** `AmbientFxSystem` co-frame przesuwa emitter
 * powyżej top edge'a viewportu. Particles są world-positioned (scrollFactor=1),
 * więc przewijają z kamerą — gracz idzie przez padający śnieg, a nie pod
 * UI-fixed overlay.
 *
 * **Performance:** density per layer to liczba live particles. Suma 3 warstw
 * = ~70 active particles for snow. Na mobile 60 FPS OK z marginesem.
 */

// Klucz presetu (`AmbientFxKey`) zdefiniowany w `src/domain/types/ambientFx.ts`,
// re-exportowany tu dla wygody konsumentów configu.
import type { AmbientFxKey } from '@/domain/types/ambientFx';
export type { AmbientFxKey };

/** Blend mode jako string — konwertowane na `Phaser.BlendModes` w systemie. */
export type AmbientBlendMode = 'NORMAL' | 'ADD' | 'SCREEN';

/**
 * Konfiguracja pojedynczej warstwy parallax.
 *
 * Konwencja warstw: tylne = wolniejsze, mniejsze, mniej widoczne, niżej w depth.
 * Przednie = szybsze, większe, jaśniejsze, wyżej w depth.
 */
export interface AmbientLayerConfig {
  /** Klucz tekstury (musi być załadowany w PreloadScene). */
  textureKey: string;

  /**
   * Liczba live particles utrzymywanych w warstwie (steady-state).
   * Emitter spawnuje `1 particle / (lifespan / density)` ms żeby ją osiągnąć.
   */
  density: number;

  /** Czas życia particle'a (ms). */
  lifespanMs: number;

  /** Bazowa pionowa prędkość spadania (px/s). Per-particle losowane w zakresie. */
  speedY: { min: number; max: number };

  /** Per-particle horizontalny random spread (px/s). Niezależny od wind. */
  speedXSpread: number;

  /** Amplituda oscylacji wiatru (px/s). Wpływa na velocityX live particles.
   *  Większa = mocniej bujają się; bliskie warstwy zazwyczaj większą. */
  windAmplitude: number;

  /** Zakres skali sprite'a. Per-particle losowane. */
  scale: { min: number; max: number };

  /** Peak alpha (po fade-in). Tylne warstwy = blade, przednie = ostre. */
  alphaPeak: number;

  /** Zakres prędkości rotacji (deg/s). 0/0 = brak rotacji (dla dot particles).
   *  Dla snowflakes typowo 30-90 deg/s. */
  rotateSpeed: { min: number; max: number };

  /** Z-ordering w scenie:
   *   bg=-100, deco=-50, dest=0, pet=5, player=6, coins=10, fx-burst=18+, popup=high
   *  Tylna warstwa: -80 (pod deco). Przednia: 12-15 (nad player, pod fx-burst). */
  depth: number;

  /** Blend mode. NORMAL dla normalnego renderingu, ADD dla glow/lekkie cząstki. */
  blendMode?: AmbientBlendMode;
}

/** Pełen preset ambient FX z wieloma warstwami + globalna konfiguracja wiatru. */
export interface AmbientFxPreset {
  /** Częstotliwość oscylacji wiatru (Hz). 0.05-0.15 = naturalny rytm porywów. */
  windFrequencyHz: number;

  /** Layers ordered back-to-front (tablica iterowana w kolejności = depth order). */
  layers: AmbientLayerConfig[];
}

// ─────────────────────────────────────────────────────────────────────────────

export const AMBIENT_FX_PRESETS: Record<AmbientFxKey, AmbientFxPreset> = {
  // ─── SNOW (mountain biome) ────────────────────────────────────────────────
  // 3 warstwy: tylne małe kropki (rozproszone), środkowe flake'i mix, przednie
  // wyraźne flake'i z rotacją. Wiatr powolny (0.08 Hz = ~12s cykl), peaks
  // na przedniej warstwie najmocniejsze (60 px/s) — szczegół, że gracz widzi
  // wyraźnie jak wiatr "łapie" cząstki przed nim, a tylne praktycznie stoją.
  snow: {
    windFrequencyHz: 0.08,
    layers: [
      // ── Warstwa daleka: duża chmura małych kropek ────────────────────────
      {
        textureKey: 'fx_snow_dot_particle',
        density: 40,
        lifespanMs: 12_000,
        speedY: { min: 18, max: 35 },
        speedXSpread: 6,
        windAmplitude: 12,
        scale: { min: 0.12, max: 0.22 },
        alphaPeak: 0.32,
        rotateSpeed: { min: 0, max: 0 },
        depth: -80,                     // pod dekoracjami (-50), nad tłem (-100)
      },
      // ── Warstwa środkowa: większe kropki + drobne flake'i ────────────────
      {
        textureKey: 'fx_snow_dot_particle',
        density: 22,
        lifespanMs: 9_000,
        speedY: { min: 40, max: 70 },
        speedXSpread: 14,
        windAmplitude: 28,
        scale: { min: 0.28, max: 0.45 },
        alphaPeak: 0.55,
        rotateSpeed: { min: 0, max: 0 },
        depth: 3,                       // nad destruktyblami (0), pod petami (5)
      },
      // ── Warstwa bliska: wyraźne snowflake'i z rotacją ────────────────────
      {
        textureKey: 'fx_snowflake_particle',
        density: 10,
        lifespanMs: 7_000,
        speedY: { min: 75, max: 130 },
        speedXSpread: 22,
        windAmplitude: 60,
        scale: { min: 0.45, max: 0.85 },
        alphaPeak: 0.78,
        rotateSpeed: { min: 25, max: 70 },
        depth: 12,                      // nad coinami (10), pod fx-burst (18+)
      },
    ],
  },

  // ─── STARWARS (Tatooine biome) — sand drift ────────────────────────────────
  // Pustynny dryf piasku: 2 warstwy piaskowych cząstek (bardziej POZIOMY ruch
  // niż śnieg — większy speedXSpread + wind) + rzadka warstwa iskier (spark, ADD
  // blend). Wiatr szybszy (0.1 Hz) niż śnieg — porywisty pustynny wiatr.
  starwars: {
    windFrequencyHz: 0.1,
    layers: [
      // ── Warstwa daleka: drobny piasek, powolny, mocno poziomy ────────────
      {
        textureKey: 'fx_starwars_sand_particle',
        density: 36,
        lifespanMs: 11_000,
        speedY: { min: 12, max: 25 },
        speedXSpread: 20,
        windAmplitude: 30,
        scale: { min: 0.10, max: 0.18 },
        alphaPeak: 0.25,
        rotateSpeed: { min: 0, max: 0 },
        depth: -80,
      },
      // ── Warstwa bliska: większe ziarna piasku ────────────────────────────
      {
        textureKey: 'fx_starwars_sand_particle',
        density: 18,
        lifespanMs: 8_000,
        speedY: { min: 30, max: 55 },
        speedXSpread: 35,
        windAmplitude: 50,
        scale: { min: 0.25, max: 0.42 },
        alphaPeak: 0.5,
        rotateSpeed: { min: 0, max: 0 },
        depth: 3,
      },
      // ── Warstwa iskier: rzadkie ciepłe błyski (ADD glow) ─────────────────
      {
        textureKey: 'fx_starwars_spark_particle',
        density: 5,
        lifespanMs: 6_000,
        speedY: { min: 20, max: 50 },
        speedXSpread: 25,
        windAmplitude: 40,
        scale: { min: 0.3, max: 0.6 },
        alphaPeak: 0.7,
        rotateSpeed: { min: 30, max: 90 },
        depth: 12,
        blendMode: 'ADD',
      },
    ],
  },

  // Biom 4 (Pixel World) — dryf unoszących się pixeli (2 warstwy) + rzadkie
  // neonowe glitche (ADD). rotateSpeed 0 wszędzie — pixel-art nie obraca się
  // płynnie. Wiatr łagodny (0.08 Hz).
  pixel: {
    windFrequencyHz: 0.08,
    layers: [
      // ── Warstwa daleka: drobne unoszące się pixele ───────────────────────
      {
        textureKey: 'fx_pixel_mote',
        density: 32,
        lifespanMs: 12_000,
        speedY: { min: 8, max: 20 },
        speedXSpread: 18,
        windAmplitude: 25,
        scale: { min: 0.10, max: 0.18 },
        alphaPeak: 0.3,
        rotateSpeed: { min: 0, max: 0 },
        depth: -80,
      },
      // ── Warstwa bliska: większe pixele ───────────────────────────────────
      {
        textureKey: 'fx_pixel_mote',
        density: 16,
        lifespanMs: 9_000,
        speedY: { min: 22, max: 45 },
        speedXSpread: 30,
        windAmplitude: 40,
        scale: { min: 0.22, max: 0.40 },
        alphaPeak: 0.55,
        rotateSpeed: { min: 0, max: 0 },
        depth: 3,
      },
      // ── Warstwa glitch: rzadkie neonowe błyski (ADD glow) ────────────────
      {
        textureKey: 'fx_pixel_glitch',
        density: 6,
        lifespanMs: 5_500,
        speedY: { min: 20, max: 50 },
        speedXSpread: 22,
        windAmplitude: 35,
        scale: { min: 0.3, max: 0.6 },
        alphaPeak: 0.75,
        rotateSpeed: { min: 0, max: 0 },
        depth: 12,
        blendMode: 'ADD',
      },
    ],
  },

  // ─── OCEAN (biom 5, Ocean / Głębiny) — dryf bąbelków + błyski kaustyk ───────
  // Widok TOP-DOWN, więc „spadanie" cząstek = dryf po tafli morza (bez grawitacji).
  // 2 warstwy bąbelków (daleka drobna + bliska większa) suną leniwie z prądem +
  // rzadka warstwa świetlnych błysków (caustic glints, ADD). Prąd wolniejszy niż
  // pustynny wiatr (0.05 Hz = ~20s cykl) — spokojny, podwodny rytm.
  ocean: {
    windFrequencyHz: 0.05,
    layers: [
      // ── Warstwa daleka: drobne bąbelki, gęste, powolny dryf ──────────────
      {
        textureKey: 'fx_ocean_bubble',
        density: 36,
        lifespanMs: 13_000,
        speedY: { min: 6, max: 16 },
        speedXSpread: 16,
        windAmplitude: 22,
        scale: { min: 0.10, max: 0.18 },
        alphaPeak: 0.28,
        rotateSpeed: { min: 0, max: 0 },
        depth: -80,
      },
      // ── Warstwa bliska: większe bąbelki ──────────────────────────────────
      {
        textureKey: 'fx_ocean_bubble',
        density: 16,
        lifespanMs: 9_000,
        speedY: { min: 18, max: 40 },
        speedXSpread: 28,
        windAmplitude: 38,
        scale: { min: 0.22, max: 0.42 },
        alphaPeak: 0.5,
        rotateSpeed: { min: 0, max: 0 },
        depth: 3,
      },
      // ── Warstwa błysków: rzadkie świetlne kaustyki (ADD glow) ─────────────
      {
        textureKey: 'fx_ocean_glimmer',
        density: 6,
        lifespanMs: 5_500,
        speedY: { min: 15, max: 40 },
        speedXSpread: 22,
        windAmplitude: 30,
        scale: { min: 0.3, max: 0.6 },
        alphaPeak: 0.7,
        rotateSpeed: { min: 0, max: 0 },
        depth: 12,
        blendMode: 'ADD',
      },
    ],
  },

  // ─── FOOD (biom 6, Fast Food / Świat Jedzenia) — dryf okruszków + posypki ───
  // Widok TOP-DOWN, więc „spadanie" cząstek = leniwy dryf po blacie. 2 warstwy
  // okruszków (daleka drobna + bliska większa) suną z lekkim wietrzykiem od
  // wentylatora kuchni + rzadka warstwa kolorowej cukrowej posypki, która
  // delikatnie błyszczy (ADD). Wiatr łagodny (0.07 Hz) — przytulny, kuchenny rytm.
  food: {
    windFrequencyHz: 0.07,
    layers: [
      // ── Warstwa daleka: drobne okruszki, gęste, powolny dryf ─────────────
      {
        textureKey: 'fx_food_crumb',
        density: 34,
        lifespanMs: 12_000,
        speedY: { min: 8, max: 18 },
        speedXSpread: 16,
        windAmplitude: 22,
        scale: { min: 0.10, max: 0.18 },
        alphaPeak: 0.30,
        rotateSpeed: { min: 0, max: 30 },
        depth: -80,
      },
      // ── Warstwa bliska: większe okruszki ─────────────────────────────────
      {
        textureKey: 'fx_food_crumb',
        density: 16,
        lifespanMs: 9_000,
        speedY: { min: 20, max: 42 },
        speedXSpread: 28,
        windAmplitude: 40,
        scale: { min: 0.24, max: 0.42 },
        alphaPeak: 0.52,
        rotateSpeed: { min: 0, max: 45 },
        depth: 3,
      },
      // ── Warstwa posypki: rzadkie kolorowe iskierki cukru (ADD glow) ───────
      {
        textureKey: 'fx_food_sprinkle',
        density: 6,
        lifespanMs: 5_500,
        speedY: { min: 16, max: 42 },
        speedXSpread: 24,
        windAmplitude: 32,
        scale: { min: 0.3, max: 0.6 },
        alphaPeak: 0.7,
        rotateSpeed: { min: 40, max: 120 },
        depth: 12,
        blendMode: 'ADD',
      },
    ],
  },

  // ── ATLANTIS (biom 7) — bąbelki (2 warstwy, dryf w górę) + złota iskierka (ADD glow) ──
  atlantis: {
    windFrequencyHz: 0.06,
    layers: [
      {
        textureKey: 'fx_atlantis_bubble',
        density: 30,
        lifespanMs: 12_000,
        speedY: { min: -14, max: -6 },
        speedXSpread: 14,
        windAmplitude: 18,
        scale: { min: 0.10, max: 0.18 },
        alphaPeak: 0.28,
        rotateSpeed: { min: 0, max: 20 },
        depth: -80,
      },
      {
        textureKey: 'fx_atlantis_bubble',
        density: 14,
        lifespanMs: 9_000,
        speedY: { min: -40, max: -18 },
        speedXSpread: 26,
        windAmplitude: 34,
        scale: { min: 0.24, max: 0.42 },
        alphaPeak: 0.5,
        rotateSpeed: { min: 0, max: 30 },
        depth: 3,
      },
      {
        textureKey: 'fx_atlantis_sparkle',
        density: 6,
        lifespanMs: 6_000,
        speedY: { min: -30, max: -10 },
        speedXSpread: 22,
        windAmplitude: 28,
        scale: { min: 0.3, max: 0.6 },
        alphaPeak: 0.7,
        rotateSpeed: { min: 30, max: 100 },
        depth: 12,
        blendMode: 'ADD',
      },
    ],
  },

  // ─── SCHOOL (biom 8) — pyłek kredy + złote gwiazdki-naklejki ───────────────
  // Odwrotnie niż w biomach podwodnych: pyłek kredy OPADA (speedY dodatnie),
  // bo unosi się w powietrzu klasy i osiada na ławce. Złote gwiazdki („piątka!")
  // lecą do góry na ADD — to jedyna warstwa, która ma się świecić.
  school: {
    windFrequencyHz: 0.05,     // leniwy przeciąg przez uchylone okno
    layers: [
      {
        textureKey: 'fx_school_chalk_dust',
        density: 28,
        lifespanMs: 13_000,
        speedY: { min: 6, max: 16 },
        speedXSpread: 16,
        windAmplitude: 22,
        scale: { min: 0.08, max: 0.16 },
        alphaPeak: 0.22,
        rotateSpeed: { min: 0, max: 15 },
        depth: -80,
      },
      {
        textureKey: 'fx_school_chalk_dust',
        density: 12,
        lifespanMs: 9_000,
        speedY: { min: 18, max: 40 },
        speedXSpread: 24,
        windAmplitude: 30,
        scale: { min: 0.2, max: 0.34 },
        alphaPeak: 0.4,
        rotateSpeed: { min: 0, max: 25 },
        depth: 3,
      },
      {
        textureKey: 'fx_school_gold_star',
        density: 5,
        lifespanMs: 6_500,
        speedY: { min: -28, max: -10 },
        speedXSpread: 20,
        windAmplitude: 26,
        scale: { min: 0.3, max: 0.6 },
        alphaPeak: 0.75,
        rotateSpeed: { min: 30, max: 110 },
        depth: 12,
        blendMode: 'ADD',
      },
    ],
  },

  // ─── ARCTIC (biom 9) — drobny śnieg, wirujące płatki i iskry lodu ─────────
  // Layout v2: ruchome bodźce celowo poniżej Szkoły (40 aktywnych cząstek
  // zamiast 45). Plansza ma interesujące statyczne klastry, więc śnieg nie
  // konkuruje z celami i etykietami. Ostatnia warstwa jako jedyna używa ADD.
  arctic: {
    windFrequencyHz: 0.07,
    layers: [
      {
        textureKey: 'fx_arctic_snow_dot',
        density: 26,
        lifespanMs: 13_000,
        speedY: { min: 14, max: 28 },
        speedXSpread: 12,
        windAmplitude: 22,
        scale: { min: 0.1, max: 0.2 },
        alphaPeak: 0.24,
        rotateSpeed: { min: 0, max: 0 },
        depth: -80,
      },
      {
        textureKey: 'fx_arctic_snowflake',
        density: 11,
        lifespanMs: 9_000,
        speedY: { min: 35, max: 65 },
        speedXSpread: 24,
        windAmplitude: 44,
        scale: { min: 0.22, max: 0.4 },
        alphaPeak: 0.48,
        rotateSpeed: { min: 25, max: 85 },
        depth: 3,
      },
      {
        textureKey: 'fx_arctic_ice_sparkle',
        density: 3,
        lifespanMs: 6_500,
        speedY: { min: -22, max: -8 },
        speedXSpread: 18,
        windAmplitude: 30,
        scale: { min: 0.28, max: 0.55 },
        alphaPeak: 0.65,
        rotateSpeed: { min: 30, max: 110 },
        depth: 12,
        blendMode: 'ADD',
      },
    ],
  },
  // ─── FOREST LEAVES (Puszcza Niepołomicka) — opadające liście + pyłki w słońcu ─
  // Puszcza to las liściasty (dąb, grab, lipa), więc zamiast śniegu/piasku leci
  // LIŚĆ: wolno, obracając się, z wyraźnym bujaniem na wietrze. Druga warstwa to
  // drobne pyłki/zarodniki złapane w przebłysku słońca — dodaje głębi bez hałasu.
  forest_leaves: {
    windFrequencyHz: 0.07,       // leniwy powiew między pniami
    layers: [
      {
        textureKey: 'fx_puszcza_leaf',
        density: 14,
        lifespanMs: 12_000,
        speedY: { min: 18, max: 42 },
        speedXSpread: 26,
        windAmplitude: 46,       // liść szeroko koszy w powietrzu
        scale: { min: 0.18, max: 0.38 },
        alphaPeak: 0.85,
        rotateSpeed: { min: 25, max: 95 },
        depth: 10,
      },
      {
        textureKey: 'fx_puszcza_pollen',
        density: 20,
        lifespanMs: 9_000,
        speedY: { min: -14, max: 10 },   // pyłek raz opada, raz unosi się w cieple
        speedXSpread: 14,
        windAmplitude: 20,
        scale: { min: 0.06, max: 0.14 },
        alphaPeak: 0.4,
        rotateSpeed: { min: 0, max: 20 },
        depth: -60,              // pod petami — mgiełka tła, nie zasłania akcji
        blendMode: 'ADD',
      },
    ],
  },
};
